/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */
import { jsx, css } from '@emotion/react'

import type { EuiBasicTableColumn } from '@elastic/eui'
import {
  EuiBasicTable,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIconTip,
  EuiText,
  EuiToolTip,
  useEuiTheme,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type {
  ResourceType,
  RoleAssignmentsTableItem,
  TableItemRoleDescriptor,
} from '@modules/role-assignments-lib/types'
import {
  isCloudSpecificProjectRoleId,
  resourceRoleLabelByRoleId,
} from '@modules/role-assignments-lib/types'
import {
  ApiAccessSelectorTitle,
  ResourceTypeDisplay,
  getCloudAPIAccessLabel,
  predefinedLabels,
} from '@modules/role-assignments-lib/messages'
import { getProjectSpecificViewerRole } from '@modules/role-assignments-lib'
import type { ProjectType } from '@modules/ui-types/projects'
import { useConfig } from '@modules/cui/ConfigContext'
import { AdminCustomRoleBadge } from '@modules/role-assignments-admin-components/ProjectRoleAssignmentsSummary/AdminCustomRoleBadge'
import { UserCustomRoleBadge } from '@modules/role-assignments-user-components/ProjectRoleAssignmentsSummary/UserCustomRoleBadge'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'

import { ApiAccessOptions } from '../ApiAccessSelector/types'
import LimitedAccessTooltip from '../ApiAccessSelector/LimitedAccessTooltip'

import type { FunctionComponent } from 'react'

const ProjectRoleAssignmentsTable: FunctionComponent<{
  items: Array<RoleAssignmentsTableItem<ResourceType>>
  customRolesEnabled: boolean
  observabilityCustomRoles: boolean
  isApiKey: boolean
}> = ({ items, customRolesEnabled, observabilityCustomRoles, isApiKey }) => {
  const isAdminConsole = useConfig('APP_NAME') === 'adminconsole'
  const [_, flags] = useFlagsWhenLoaded()
  const isUIAMEnabled = flags.uiamUi
  const { euiTheme } = useEuiTheme()

  return (
    <EuiBasicTable
      data-test-id='instance-role-assignments-table'
      noItemsMessage={
        <FormattedMessage
          id='roles-assignments.no-instance-found'
          defaultMessage='No cloud resource found'
        />
      }
      items={items}
      columns={getColumns(
        customRolesEnabled,
        observabilityCustomRoles,
        isAdminConsole,
        isUIAMEnabled,
        euiTheme,
        isApiKey,
      )}
    />
  )
}

const getColumns = (
  customRolesEnabled: boolean,
  observabilityCustomRoles: boolean,
  isAdminConsole: boolean,
  isUIAMEnabled: boolean,
  euiTheme: ReturnType<typeof useEuiTheme>['euiTheme'],
  isApiKey: boolean,
): Array<EuiBasicTableColumn<RoleAssignmentsTableItem<ResourceType>>> => {
  const columns = [
    {
      name: <FormattedMessage id='roles-assignments.name' defaultMessage='Name' />,
      field: 'name',
      sortable: ({ name }) => name.toLowerCase(),
      render: (name: string) => (
        <EuiToolTip content={name} position='left' display='block'>
          <span style={{ display: 'block' }} className='eui-textTruncate'>
            {name}
          </span>
        </EuiToolTip>
      ),
      truncateText: true,
      width: isUIAMEnabled ? '25%' : '30%',
    },
    {
      name: <FormattedMessage id='roles-assignments.type' defaultMessage='Type' />,
      field: 'resourceType',
      render: (resourceType: ResourceType) => (
        <ResourceTypeDisplay resourceType={resourceType} capitalize={true} />
      ),
      width: isUIAMEnabled ? '20%' : '25%',
    },
    {
      name: (
        <FormattedMessage
          id='roles-assignments.roleColumn'
          defaultMessage='{count, plural, one {Role} other {Roles}}'
          values={{ count: customRolesEnabled ? 2 : 1 }}
        />
      ),
      field: 'roles',
      render: (
        roles: TableItemRoleDescriptor[],
        roleAssignmentItem: RoleAssignmentsTableItem<ResourceType>,
      ) => {
        const roleToProcess = customRolesEnabled ? roles : roles.slice(0, 1)
        const roleItems = roleToProcess.map((roleDescriptor) => {
          if (customRolesEnabled && roleDescriptor.isCustomRole) {
            return isAdminConsole ? (
              <AdminCustomRoleBadge
                key={roleAssignmentItem.id.concat(roleDescriptor.roleId)}
                item={roleAssignmentItem}
                roleDescriptor={roleDescriptor}
              />
            ) : (
              <UserCustomRoleBadge
                key={roleAssignmentItem.id.concat(roleDescriptor.roleId)}
                item={roleAssignmentItem}
                roleDescriptor={roleDescriptor}
                customRolesEnabled={customRolesEnabled}
                observabilityCustomRoles={observabilityCustomRoles}
              />
            )
          }

          // If we encounter a custom role when custom roles is disabled,
          // override it with the appropriate viewer predefined role
          const predefinedRoleId = roleDescriptor.isCustomRole
            ? getProjectSpecificViewerRole(roleAssignmentItem.resourceType as ProjectType)
            : roleDescriptor.roleId

          const roleLabel = resourceRoleLabelByRoleId[predefinedRoleId]

          const predefinedRoleLabels = predefinedLabels[roleAssignmentItem.resourceType]

          return (
            <EuiFlexItem grow={false} key={roleAssignmentItem.id.concat(roleDescriptor.roleId)}>
              <FormattedMessage {...predefinedRoleLabels[roleLabel]} />
            </EuiFlexItem>
          )
        })

        return (
          <EuiFlexGroup direction='column' justifyContent='flexStart' gutterSize='xs'>
            {roleItems}
          </EuiFlexGroup>
        )
      },
      width: isUIAMEnabled ? '20%' : '25%',
    },
  ]

  if (isUIAMEnabled) {
    columns.push({
      name: <ApiAccessSelectorTitle isApiKey={isApiKey} isOrgOwner={false} />,
      field: 'roles',
      render: (
        roles: TableItemRoleDescriptor[],
        roleAssignmentItem: RoleAssignmentsTableItem<ResourceType>,
      ) => {
        const containsCustomRoles = roles.some((role) => role.isCustomRole)
        const containsNonCloudSpecificProjectRoles = roles.some(
          (role) => !isCloudSpecificProjectRoleId(role.roleId),
        )
        const shouldShowTooltip =
          containsCustomRoles &&
          roleAssignmentItem.apiAccess === ApiAccessOptions.CloudApi &&
          containsNonCloudSpecificProjectRoles
        return (
          <EuiFlexGroup direction='column' justifyContent='flexStart' gutterSize='xs'>
            {roleAssignmentItem.apiAccess ? (
              <EuiFlexGroup justifyContent='flexStart' gutterSize='s'>
                <EuiFlexItem grow={false}>
                  <FormattedMessage
                    {...getCloudAPIAccessLabel({
                      apiAccess: roleAssignmentItem.apiAccess,
                      isApiKey,
                    })}
                  />
                </EuiFlexItem>
                <EuiFlexItem grow={false} css={css({ alignSelf: 'center' })}>
                  {shouldShowTooltip && (
                    <EuiIconTip
                      type='warning'
                      color='warning'
                      content={<LimitedAccessTooltip isApiKey={isApiKey} />}
                      position='top'
                      iconProps={{
                        css: {
                          display: 'block',
                        },
                      }}
                      css={css({ backgroundColor: euiTheme.colors.backgroundBasePlain })}
                    />
                  )}
                </EuiFlexItem>
              </EuiFlexGroup>
            ) : (
              <EuiText size='s' color='subdued'>
                <FormattedMessage
                  id='roles-assignments.not-supported.access.label'
                  defaultMessage='Not supported'
                />
              </EuiText>
            )}
          </EuiFlexGroup>
        )
      },
      width: '25%',
    })
  }

  return columns
}

export default ProjectRoleAssignmentsTable
